// apps/mcv-client/src/features/vault/dashboard/CompletenessPanel.tsx
//
// MCV-1422: Hosts the Next Steps suggestion list. Replaces the legacy
// "[Coming soon]" placeholder. Pairs with QuickStatsPanel in the dashboard
// summary strip and matches its outer shape (rounded, border, min-h-40).
//
// The full-width ProfileSuggestionsPanel that previously rendered between
// the summary strip and the category grid is retired; its state machine
// (profileSuggestions.machine) drives this panel instead.

import { useActorRef, useSelector } from '@xstate/react';
import { useEffect } from 'react';
import { useNavigate } from 'react-router';
import { LoadingSpinner } from '~/components';
import {
  profileSuggestionsMachine,
  selectCanRefresh,
  selectFeatureEnabled,
  selectSuggestions,
} from '../profileSuggestions/profileSuggestions.machine';

interface CompletenessPanelProps {
  // The completeness data the panel was originally designed for is unused
  // while the suggestions implementation owns this space. Kept on the
  // interface so callers don't need to change, and so the future
  // completeness UI can slot back in without an upstream refactor.
  percentage: number;
  verified: number;
  total: number;
  categories: { name: string; verified: boolean }[];
  providerId?: number;
  enabled?: boolean;
}

export const CompletenessPanel = ({ providerId, enabled = true }: CompletenessPanelProps) => {
  const navigate = useNavigate();
  const actorRef = useActorRef(profileSuggestionsMachine, {
    input: { providerId: providerId || 0 },
  });
  const featureEnabled = useSelector(actorRef, selectFeatureEnabled);
  const suggestions = useSelector(actorRef, selectSuggestions);
  const canRefresh = useSelector(actorRef, selectCanRefresh);
  const stateValue = useSelector(actorRef, (state) => state.value);
  const isLoading = stateValue === 'Loading' || stateValue === 'Refreshing';
  const isAwaitingRefresh = stateValue === 'AwaitingRefresh';

  useEffect(() => {
    if (providerId && enabled) {
      actorRef.send({ type: 'LOAD', providerId });
    }
  }, [actorRef, enabled, providerId]);

  // MCV-1823: a failed load or refresh shows nothing of its own. A failed refresh leaves the
  // suggestions already loaded as they were. A failed load leaves an empty list, which shows the
  // normal empty state with Refresh disabled. The failure is logged by the API instead.
  const showFeatureBody = Boolean(providerId) && enabled && featureEnabled;
  // MCV-1846: Refresh is offered only when the API says a refresh is available. It is withheld for a
  // while after any vault commit (including document upload and parse), so a greyed-out button
  // would only invite clicks that do nothing.

  return (
    <div
      data-cy="dashboard-next-steps-panel"
      className="bg-white rounded-xl border border-grey-900 shadow-sm p-6 min-h-40 flex flex-col"
    >
      <div className="flex items-center justify-center relative mb-3">
        <h3 className="font-semibold text-sm text-grey-400 uppercase tracking-wider text-center">
          Next Steps
        </h3>
        {showFeatureBody && canRefresh ? (
          <button
            type="button"
            className="absolute right-0 text-xs font-medium text-primary-50 cursor-pointer disabled:text-grey-500 disabled:cursor-default"
            disabled={isLoading || isAwaitingRefresh}
            onClick={() => actorRef.send({ type: 'REFRESH' })}
            data-cy="dashboard-next-steps-refresh"
          >
            Refresh
          </button>
        ) : null}
      </div>

      {!showFeatureBody ? (
        <p className="text-sm text-grey-400 italic text-center my-auto">[Coming soon]</p>
      ) : isLoading && suggestions.length === 0 ? (
        <div className="flex justify-center py-3">
          <LoadingSpinner size={24} />
        </div>
      ) : suggestions.length === 0 ? (
        <p className="text-sm text-grey-400 italic text-center my-auto">
          {isAwaitingRefresh ? 'Updating your next steps...' : 'No suggestions yet.'}
        </p>
      ) : (
        <ul
          className="overflow-y-auto max-h-40 divide-y divide-grey-900/40 pr-1"
          data-cy="dashboard-next-steps-list"
        >
          {suggestions.map((suggestion) => (
            <li key={suggestion.id}>
              <button
                type="button"
                className="w-full text-left py-2 px-1 hover:bg-grey-900/20 rounded-sm transition-colors cursor-pointer"
                onClick={() => navigate(suggestion.targetRoute || '/vault/profile')}
              >
                <div className="flex items-baseline gap-2">
                  <span className="text-xs font-semibold text-primary-50 shrink-0">
                    Step {suggestion.rank}
                  </span>
                  <span className="font-semibold text-sm text-grey-100">{suggestion.title}</span>
                </div>
                <p className="text-xs text-grey-400 mt-0.5 leading-snug line-clamp-2">
                  {suggestion.body}
                </p>
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};
